import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to handle automatic page selection when no page is in the URL
 * Shared between viewCourse (student view) and editCourse (instructor edit view)
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isLoading - Whether course data is loading
 * @param {boolean} params.isFetching - Whether course data is fetching (optional, for viewCourse)
 * @param {string|number} params.urlPageId - Page ID from URL parameters
 * @param {Object} params.courseData - Course data object with toc and lastViewedPageId
 * @param {Function} params.navigate - React Router navigate function
 * @param {number|string} params.courseId - Course ID
 * @param {Function} params.setCurrentPageId - Function to set current page ID in state
 * @param {string} params.basePath - Base path for navigation (e.g., '/view/course' or '/edit/course')
 * @returns {Object} - { hasRedirectedToInitialPage, resetRedirect }
 */
export const useAutoPageSelection = ({
  isLoading,
  isFetching = false,
  urlPageId,
  courseData,
  navigate,
  courseId,
  setCurrentPageId,
  basePath
}) => {
  const [hasRedirectedToInitialPage, setHasRedirectedToInitialPage] = useState(false);

  // Automatic default page selection
  // When no pageId in URL, navigate to last viewed page or first available content page
  useEffect(() => {
    // Wait for loading/fetching to complete to avoid using stale data
    if (!isLoading && !isFetching && !urlPageId && courseData && !hasRedirectedToInitialPage) {
      // Find first content section with pages (excluding diploma)
      const firstContentSection = Array.isArray(courseData.toc)
        ? courseData.toc.find((s) => Array.isArray(s.pages) && s.pages.length > 0 && s.pages[0]?.id !== 'diploma')
        : null;

      // Prefer lastViewedPageId (where user left off), fallback to first page
      const fallbackId = courseData.lastViewedPageId || firstContentSection?.pages?.[0]?.id || 'diploma';

      // DEBUG: Log when auto-selecting page (no pageId in URL)
      console.log('[DEBUG-PAGE] Auto-select:', { courseId, lastViewedPageId: courseData.lastViewedPageId, firstPage: firstContentSection?.pages?.[0]?.id, selected: fallbackId });

      if (fallbackId) {
        setCurrentPageId(fallbackId);
        navigate(`${basePath}/${courseId}/${fallbackId}`, { replace: true });
        setHasRedirectedToInitialPage(true);
      }
    }
  }, [isLoading, isFetching, urlPageId, courseData, hasRedirectedToInitialPage, navigate, courseId, setCurrentPageId, basePath]);

  // Reset redirect guard when course changes or when user manually changes URL
  useEffect(() => {
    setHasRedirectedToInitialPage(false);
  }, [courseId, urlPageId]);

  return {
    hasRedirectedToInitialPage,
    resetRedirect: useCallback(() => setHasRedirectedToInitialPage(false), [])
  };
};

